import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit2, Trash2, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';
import useRestaurants from '../../hooks/useRestaurants';
import { updateRestaurant } from '../../api/restaurantsApi';

/**
 * UsersRolesTab Component
 * 
 * Manages user access, roles, and permissions for location staff.
 */
const UsersRolesTab = () => {
  const { selectedLocationId, setSelectedLocationId } = useSettings();
  const { restaurants, refetch } = useRestaurants();
  const [users, setUsers] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showRolesAccordion, setShowRolesAccordion] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Auto-select first location if "all" is selected (Users tab needs a specific location)
  useEffect(() => {
    if (selectedLocationId === 'all' && restaurants && restaurants.length > 0) {
      setSelectedLocationId(restaurants[0].locationId);
    }
  }, [selectedLocationId, restaurants, setSelectedLocationId]);

  // Load users from restaurant data
  useEffect(() => {
    if (selectedLocationId && restaurants) {
      const restaurant = restaurants.find(r => r.locationId === selectedLocationId);
      if (restaurant && restaurant.users && Array.isArray(restaurant.users)) {
        setUsers(restaurant.users);
      } else {
        // Default empty array if no users
        setUsers([]);
      }
    }
  }, [selectedLocationId, restaurants]);

  // Add User Form State
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'Viewer'
  });

  // Handle add user
  const handleAddUser = async () => {
    if (!selectedLocationId) {
      alert('Please select a restaurant in the Organization tab first.');
      return;
    }

    if (newUser.name && newUser.email) {
      const user = {
        id: Date.now(),
        ...newUser,
        status: 'Pending'
      };
      const updatedUsers = [...users, user];
      setUsers(updatedUsers);
      setNewUser({ name: '', email: '', role: 'Viewer' });
      setShowAddUserModal(false);

      try {
        await updateRestaurant(selectedLocationId, { users: updatedUsers });
        
        // Refresh restaurant data from DynamoDB
        if (refetch) {
          await refetch();
        }
        
        alert(`User ${user.name} has been added. They can now sign in with Google or Microsoft using their email: ${user.email}`);
      } catch (error) {
        console.error('Error saving user:', error);
        alert(error.message || 'Failed to save user. Please try again.');
        // Revert on error
        setUsers(users);
      }
    }
  };

  // Handle edit user
  const handleEditUser = (user) => {
    setEditingUser(user);
    setNewUser({ name: user.name, email: user.email, role: user.role });
    setShowAddUserModal(true);
  };

  // Handle update user
  const handleUpdateUser = async () => {
    if (!selectedLocationId) {
      alert('Please select a location in the Organization tab first.');
      return;
    }

    if (editingUser && newUser.name && newUser.email) {
      const updatedUsers = users.map(u => 
        u.id === editingUser.id 
          ? { ...u, ...newUser }
          : u
      );
      setUsers(updatedUsers);
      setEditingUser(null);
      setNewUser({ name: '', email: '', role: 'Viewer' });
      setShowAddUserModal(false);

      try {
        await updateRestaurant(selectedLocationId, { users: updatedUsers });
        
        // Refresh restaurant data from DynamoDB
        if (refetch) {
          await refetch();
        }
        
        alert('User updated successfully!');
      } catch (error) {
        console.error('Error updating user:', error);
        alert(error.message || 'Failed to update user. Please try again.');
        // Revert on error
        setUsers(users);
      }
    }
  };

  // Handle remove user
  const handleRemoveUser = async (userId) => {
    if (!selectedLocationId) {
      alert('Please select a location in the Organization tab first.');
      return;
    }

    if (window.confirm('Are you sure you want to remove this user?')) {
      const updatedUsers = users.filter(u => u.id !== userId);
      setUsers(updatedUsers);

      try {
        await updateRestaurant(selectedLocationId, { users: updatedUsers });
        
        // Refresh restaurant data from DynamoDB
        if (refetch) {
          await refetch();
        }
        
        alert('User removed successfully!');
      } catch (error) {
        console.error('Error removing user:', error);
        alert(error.message || 'Failed to remove user. Please try again.');
        // Revert on error
        setUsers(users);
      }
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowAddUserModal(false);
    setEditingUser(null);
    setNewUser({ name: '', email: '', role: 'Viewer' });
  };

  if (!selectedLocationId) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-400">Please select a location in the Organization tab first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Users Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Users</h3>
              <p className="text-sm text-gray-400">Manage access for location staff</p>
            </div>
            <Button
              onClick={() => setShowAddUserModal(true)}
              className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-400 py-8">
                    <div className="space-y-2">
                      <p>No users found. Click "Add User" to add someone.</p>
                      <p className="text-xs text-gray-500">Note: Users must sign in with Google or Microsoft using the email address you add.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium text-white">{user.name}</TableCell>
                    <TableCell className="text-gray-300">{user.email}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.role === 'Admin' 
                          ? 'bg-purple-900/20 text-purple-400 border border-purple-700/50'
                          : user.role === 'Manager'
                          ? 'bg-blue-900/20 text-blue-400 border border-blue-700/50'
                          : 'bg-gray-700 text-gray-300 border border-gray-600'
                      }`}>
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.status === 'Active'
                          ? 'bg-green-900/20 text-green-400 border border-green-700/50'
                          : 'bg-yellow-900/20 text-yellow-400 border border-yellow-700/50'
                      }`}>
                        {user.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          className="text-gray-400 hover:text-white hover:bg-gray-700"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveUser(user.id)}
                          className="text-gray-400 hover:text-red-400 hover:bg-gray-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Roles Summary Accordion */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <button
            onClick={() => setShowRolesAccordion(!showRolesAccordion)}
            className="flex items-center justify-between w-full text-left"
          >
            <div>
              <h3 className="text-lg font-semibold text-white">Roles Summary</h3>
              <p className="text-sm text-gray-400">Understand what each role can access</p>
            </div>
            {showRolesAccordion ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
        </CardHeader>
        {showRolesAccordion && (
          <CardContent className="space-y-4 pt-0">
            <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
              <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                <span className="px-2 py-1 rounded text-xs bg-purple-900/20 text-purple-400 border border-purple-700/50">
                  Admin
                </span>
              </h4>
              <p className="text-sm text-gray-300">
                Full access to all features including settings, billing, user management, and analytics.
              </p>
            </div>
            <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
              <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                <span className="px-2 py-1 rounded text-xs bg-blue-900/20 text-blue-400 border border-blue-700/50">
                  Manager
                </span>
              </h4>
              <p className="text-sm text-gray-300">
                Access to calls, analytics, and voice settings. Cannot manage users or billing.
              </p>
            </div>
            <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
              <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                <span className="px-2 py-1 rounded text-xs bg-gray-700 text-gray-300 border border-gray-600">
                  Viewer
                </span>
              </h4>
              <p className="text-sm text-gray-300">
                View-only access to calls and analytics. Cannot make changes or access settings.
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-gray-700 w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  {editingUser ? 'Edit User' : 'Add User'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="mb-4 p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                <p className="text-blue-400 text-xs font-semibold mb-1">ℹ️ How Users Sign In</p>
                <p className="text-blue-300/80 text-xs">
                  After adding a user, they can sign in using Google or Microsoft with the email address you provide. Make sure they use the exact email you enter here.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Name
                </label>
                <Input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">User must sign in with this email via Google or Microsoft</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Role
                </label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleCloseModal}
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={editingUser ? handleUpdateUser : handleAddUser}
                  className="flex-1 bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                  disabled={!newUser.name || !newUser.email}
                >
                  {editingUser ? 'Update' : 'Invite'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UsersRolesTab;

